import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useTheme } from "@/components/ThemeProvider";
import {
  DashboardMockup, JobsMockup, InvoicesMockup, PricebookMockup, AnalyticsMockup
} from "@/components/landing/LandingMockups";
import {
  Gauge, BarChart3, FileText, ArrowRight, CheckCircle,
  Calendar, Users, CreditCard, Shield, Star, Zap, Clock,
  Wrench, Thermometer, Plug, Play, ChevronLeft, ChevronRight,
  X, Sparkles, DollarSign, Bell, PieChart, Sun, Moon,
  AlertTriangle, TrendingUp, ClipboardList, Receipt, UserCheck,
  ChevronDown, Target, HardHat, Layers, MessageSquare, Truck,
  BookOpen, BarChart, Settings, Phone, Lock, CircleDollarSign,
  ArrowUpRight, Minus, Hash, Timer, ShieldCheck, Package,
  Hammer, Paintbrush, Home, TreePine, Eye, Brain, Radio
} from "lucide-react";
import TradeIQIcon from "@/components/icons/TradeIQIcon";
import Seo from "@/components/Seo";

// ── Intersection Observer Hook ──
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, isInView];
};

// ── Animated Counter ──
const Counter = ({ target, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView();
  useEffect(() => {
    if (!inView) return;
    const num = parseInt(target.replace(/[^0-9]/g, ""), 10);
    const step = Math.max(1, Math.floor(num / 40));
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= num) { setCount(num); clearInterval(timer); }
      else setCount(current);
    }, 30);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ── Product Tour Slides ──
const tourMockups = {
  dashboard: DashboardMockup,
  jobs: JobsMockup,
  invoices: InvoicesMockup,
  pricebook: PricebookMockup,
  analytics: AnalyticsMockup,
};

const tourSlides = [
  { id: 1, title: "Job Command Center", subtitle: "Total job visibility", description: "Every detail of a job - tasks, materials, crew, costs, profit - on a single screen. No more digging through spreadsheets.", features: ["Full job lifecycle tracking", "Real-time profit per job", "Task & material management", "Crew assignments"], icon: Target, color: "from-brand-500 to-amber-500", mockupKey: "dashboard" },
  { id: 2, title: "Drag-and-Drop Scheduling", subtitle: "Dispatch made simple", description: "See your week at a glance. Drag jobs onto your crew's calendar. Avoid conflicts. Stop double-booking.", features: ["Visual drag-and-drop", "Crew availability", "Conflict detection", "Calendar sync"], icon: Calendar, color: "from-emerald-500 to-teal-500", mockupKey: "jobs" },
  { id: 3, title: "Invoicing & Payments", subtitle: "Get paid faster", description: "One-click invoicing from completed jobs. Accept card, Zelle, cash, or check. Automated reminders chase the money so you don't have to.", features: ["One-click invoicing", "Automated payment reminders", "Multiple payment methods", "Real-time payment tracking"], icon: Receipt, color: "from-blue-500 to-cyan-500", mockupKey: "invoices" },
  { id: 4, title: "AI-Powered Pricebook", subtitle: "Your smartest hire", description: "Skilled trades-specific pricebook templates auto-populated when you sign up. AI Pricing Advisor analyzes your margins and suggests competitive adjustments.", features: ["Skilled trades-specific default items", "AI pricing recommendations", "Import from other skilled trades", "Margin health scoring"], icon: TradeIQIcon, color: "from-violet-500 to-purple-500", mockupKey: "pricebook" },
  { id: 5, title: "Job Tracking", subtitle: "Know your numbers", description: "See real-time job costs on every single job. Compare estimated vs. actual costs as work progresses. Stay on top of your margins.", features: ["Live job costing", "Cost vs estimate tracking", "Revenue analytics", "Weekly performance reports"], icon: TrendingUp, color: "from-rose-500 to-pink-500", mockupKey: "analytics" },
];

// AppScreenshot component removed — replaced by CSS mockups

// ── Product Tour Modal ──
const ProductTourModal = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isOpen || !isAutoPlaying) return;
    const timer = setInterval(() => setCurrentSlide((p) => (p + 1) % tourSlides.length), 5000);
    return () => clearInterval(timer);
  }, [isOpen, isAutoPlaying]);

  useEffect(() => { if (isOpen) { setCurrentSlide(0); setIsAutoPlaying(true); } }, [isOpen]);

  const slide = tourSlides[currentSlide];
  const Icon = slide.icon;
  const isLast = currentSlide === tourSlides.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#0a0f1e] border-slate-800" aria-describedby={undefined}>
        <div className="sr-only">{slide.title}</div>
        <div className="relative">
          <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[500px]">
            <div className="p-8 flex flex-col justify-center">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-4 bg-gradient-to-r ${slide.color} text-white w-fit`}>
                <Icon className="h-4 w-4" />{slide.subtitle}
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">{slide.title}</h2>
              <p className="text-slate-400 mb-6">{slide.description}</p>
              <ul className="space-y-2 mb-8">
                {slide.features.map((f, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300"><CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />{f}</li>
                ))}
              </ul>
              {isLast && (
                <Link to="/signup" onClick={onClose}>
                  <Button className="w-full bg-brand-500 hover:bg-brand-600 text-white gap-2 mb-4 h-12 text-lg"><Sparkles className="h-5 w-5" />Get Started Free</Button>
                </Link>
              )}
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  {tourSlides.map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <button key={i} onClick={() => { setCurrentSlide(i); setIsAutoPlaying(false); }} className={`h-2 rounded-full transition-all ${i === currentSlide ? "w-8 bg-brand-500" : "w-2 bg-slate-600 hover:bg-slate-500"}`} />
                  ))}
                </div>
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => { setCurrentSlide((p) => (p - 1 + tourSlides.length) % tourSlides.length); setIsAutoPlaying(false); }} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"><ChevronLeft className="h-5 w-5 text-white" /></button>
                  <button onClick={() => { setCurrentSlide((p) => (p + 1) % tourSlides.length); setIsAutoPlaying(false); }} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"><ChevronRight className="h-5 w-5 text-white" /></button>
                </div>
              </div>
            </div>
            <div className={`bg-gradient-to-br ${slide.color} p-6 flex items-center justify-center`}>
              <div className="w-full">
                {(() => {
                  const Mockup = tourMockups[slide.mockupKey];
                  return Mockup ? <Mockup /> : null;
                })()}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ── Pricing Tiers Data ──
const pricingTiers = [
  {
    id: "starter",
    name: "Starter",
    price: 79,
    tagline: "For solo contractors and small shops",
    description: "Everything you need to stop the chaos and start running organized.",
    cta: "Get Started",
    features: [
      "Job scheduling & creation",
      "Basic invoicing & payments",
      "Crew mobile view",
      "Basic CRM & customer records",
      "Quote & contract builder",
      "Up to 2 users",
    ],
    highlighted: false,
  },
  {
    id: "professional",
    name: "Growth",
    price: 129,
    tagline: "For shops with 2-6 techs. Best value.",
    description: "Full visibility into your operation. Manage your team, impress your customers.",
    cta: "Get Started",
    features: [
      "Everything in Starter",
      "Full CRM with customer history",
      "Quotes, contracts & change orders",
      "Crew management & dispatch",
      "In-app messaging",
      "Business dashboard & reporting",
      "Automated payment reminders",
      "Up to 10 users",
    ],
    highlighted: true,
    badge: "MOST POPULAR",
  },
  {
    id: "enterprise",
    name: "Pro",
    price: 199,
    tagline: "For serious operators who want every edge",
    description: "AI-powered pricing, deep job costing, and advanced analytics to run smarter.",
    cta: "Get Started",
    features: [
      "Everything in Growth",
      "TradeIQ AI assistant",
      "Job costing & tracking",
      "Pricebook system",
      "Advanced reporting & KPIs",
      "Revenue per tech analytics",
      "Priority support",
      "Unlimited users",
    ],
    highlighted: false,
  },
  {
    id: "elite",
    name: "Elite",
    price: 299,
    tagline: "For scaling companies building an empire",
    description: "Enterprise features for contractors ready to dominate their market.",
    cta: "Get Started",
    features: [
      "Everything in Pro",
      "Service agreements & recurring revenue",
      "Customer portal (pay online, book jobs)",
      "Inventory & purchase orders",
      "Custom branding & white-label",
      "QuickBooks & calendar integrations",
    ],
    highlighted: false,
  },
];


// ══════════════════════════════════════════
//  LANDING PAGE
// ══════════════════════════════════════════
const Landing = () => {
  const [tourOpen, setTourOpen] = useState(false);
  const { theme, setTheme, toggleTheme } = useTheme();

  // Default landing page to light mode if no saved preference
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (!saved) {
      setTheme("light");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDark = theme === "dark";

  const [heroRef, heroInView] = useInView();
  const [problemRef, problemInView] = useInView();
  const [solutionRef, solutionInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [tradeIQRef, tradeIQInView] = useInView();
  const [previewRef, previewInView] = useInView();
  const [pricingRef, pricingInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <div className={`min-h-screen overflow-x-hidden transition-colors duration-300 ${isDark ? "bg-[#060a14] text-white" : "bg-white text-gray-900"}`}>
      <style>{`
        .landing { --lp-text: ${isDark ? "#fff" : "#111827"}; --lp-muted: ${isDark ? "#94a3b8" : "#6b7280"}; --lp-subtle: ${isDark ? "#64748b" : "#9ca3af"}; --lp-border: ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)"}; --lp-card: ${isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}; --lp-card-hover: ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}; --lp-section: ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"}; --lp-float: ${isDark ? "#0f1729" : "#ffffff"}; --lp-float-border: ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}; }
        .landing .lp-text { color: var(--lp-text); }
        .landing .lp-muted { color: var(--lp-muted); }
        .landing .lp-subtle { color: var(--lp-subtle); }
        .landing .lp-border { border-color: var(--lp-border); }
        .landing .lp-card { background: var(--lp-card); border-color: var(--lp-border); }
        .landing .lp-card:hover { background: var(--lp-card-hover); }
        .landing .lp-section { background: var(--lp-section); }
        .landing .lp-float { background: var(--lp-float); border-color: var(--lp-float-border); box-shadow: ${isDark ? "0 25px 50px -12px rgba(0,0,0,0.5)" : "0 25px 50px -12px rgba(0,0,0,0.1)"}; }
      `}</style>
      <div className="landing">
      <Seo
        title="SkilledShark | Field Service Management Software for Trades"
        description="Run your plumbing, HVAC, or electrical business with SkilledShark — job scheduling, invoicing, CRM, quotes, and AI tools built for skilled trades."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://skilledshark.com/#organization",
                name: "SkilledShark",
                url: "https://skilledshark.com/",
                logo: "https://skilledshark.com/logo-light.png",
                description:
                  "Field service management software for plumbing, HVAC, and electrical trade businesses.",
                areaServed: "US",
                contactPoint: {
                  "@type": "ContactPoint",
                  email: "support@skilledsharkapp.com",
                  contactType: "customer support",
                },
              },
              {
                "@type": "SoftwareApplication",
                name: "SkilledShark",
                url: "https://skilledshark.com/",
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web, iOS, Android",
                description:
                  "All-in-one field service management for skilled trades: job scheduling, invoicing, quotes, CRM, and AI-powered tools.",
                publisher: { "@id": "https://skilledshark.com/#organization" },
                offers: {
                  "@type": "AggregateOffer",
                  priceCurrency: "USD",
                  lowPrice: "79",
                  highPrice: "299",
                  offerCount: "4",
                },
              },
            ],
          }),
        }}
      />

      {/* ── HEADER ── */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-colors duration-300 ${isDark ? "bg-[#060a14]/90 border-white/5" : "bg-white/90 border-gray-200"}`}>
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between max-w-7xl">
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <img src={isDark ? "/logo-dark.png?v=3" : "/logo-light.png?v=3"} alt="SkilledShark" className="h-6 sm:h-8 md:h-12 w-auto object-contain max-w-[150px] sm:max-w-[210px] md:max-w-none" />
          </Link>
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            <a href="#features" className={`text-sm px-3 py-2 transition-colors ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>Features</a>
            <a href="#pricing" className={`text-sm px-3 py-2 transition-colors ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>Pricing</a>
            <Button variant="ghost" className={`gap-2 text-sm ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`} onClick={() => setTourOpen(true)} data-testid="nav-demo-btn">
              <Play className="h-4 w-4" />See It In Action
            </Button>
            <div className={`w-px h-5 mx-1 ${isDark ? "bg-white/10" : "bg-gray-200"}`} />
            <button onClick={toggleTheme} className={`p-2 rounded-lg transition-colors ${isDark ? "text-slate-400 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`} data-testid="landing-theme-toggle">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/login"><Button variant="ghost" className={`text-sm ${isDark ? "text-slate-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`} data-testid="login-nav-btn">Log In</Button></Link>
            <Link to="/signup"><Button className="bg-brand-500 hover:bg-brand-600 text-white text-sm px-5 font-semibold" data-testid="signup-nav-btn">Get Started</Button></Link>
          </div>
          {/* Mobile Nav */}
          <div className="flex md:hidden items-center gap-1.5 shrink-0">
            <button onClick={toggleTheme} className={`p-2 rounded-lg ${isDark ? "text-slate-400" : "text-gray-500"}`} data-testid="landing-theme-toggle-mobile">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/login"><Button variant="ghost" size="sm" className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Log In</Button></Link>
            <Link to="/signup"><Button size="sm" className="bg-brand-500 hover:bg-brand-600 text-white text-xs px-3 font-semibold">Get Started</Button></Link>
          </div>
        </div>
      </header>

      {/* ═══════ 1. HERO SECTION ═══════ */}
      <section ref={heroRef} className="relative pt-12 sm:pt-16 lg:pt-20 pb-14 sm:pb-16 px-4 sm:px-6 overflow-hidden">
        {/* Grid bg */}
        <div className={`absolute inset-0 ${isDark ? "bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)]" : "bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)]"} bg-[size:64px_64px]`} />
        {/* Orange glow */}
        <div className={`absolute top-0 right-0 w-[700px] h-[700px] rounded-full blur-[150px] -translate-y-1/3 translate-x-1/4 ${isDark ? "bg-brand-500/[0.04]" : "bg-brand-500/[0.06]"}`} />
        <div className={`absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[120px] translate-y-1/3 -translate-x-1/4 ${isDark ? "bg-blue-500/[0.03]" : "bg-blue-500/[0.04]"}`} />

        <div className="container mx-auto max-w-7xl relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Copy */}
            <div className={`space-y-8 transition-all duration-1000 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <div className="inline-flex items-center gap-2 bg-brand-500/10 text-brand-400 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border border-brand-500/20">
                <HardHat className="h-3.5 w-3.5" />
                Built for Every Skilled Trade
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" data-testid="hero-headline">
                Run Your Entire Skilled Trades Business{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 to-brand-300">From One Screen.</span>
              </h1>

              <p className={`text-base sm:text-lg max-w-xl leading-relaxed ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                Schedule jobs. Dispatch crews. Track profit on every call. Send invoices and get paid &mdash; all without the spreadsheets, sticky notes, or chaos.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/signup" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-brand-500 hover:bg-brand-600 text-white gap-2 h-13 sm:h-14 px-8 text-base sm:text-lg font-bold" data-testid="hero-cta-btn">
                    Get Started Free <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className={`h-13 sm:h-14 px-8 text-base sm:text-lg gap-2 font-medium ${isDark ? "border-white/10 hover:bg-white/5 text-white" : "border-gray-200 hover:bg-gray-50 text-gray-700"}`}
                  onClick={() => setTourOpen(true)}
                  data-testid="hero-demo-btn"
                >
                  <Play className="h-5 w-5 text-brand-400" />
                  See It In Action
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" style={{color: "var(--lp-subtle)"}}>
                {["7-day free trial", "No credit card required", "Setup in 5 minutes"].map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500/70" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Dashboard Preview */}
            <div className={`relative transition-all duration-1000 delay-200 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="relative cursor-pointer group" onClick={() => setTourOpen(true)}>
                <DashboardMockup />
                <div className="absolute inset-0 rounded-xl bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white rounded-full p-4 shadow-xl"><Play className="h-8 w-8 text-brand-500 fill-brand-500" /></div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -bottom-5 -left-4 sm:-left-8 rounded-xl p-3.5 animate-float-solid z-10" style={{background: isDark ? "#0f1729" : "#ffffff", border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)", boxShadow: isDark ? "0 25px 50px -12px rgba(0,0,0,0.5)" : "0 8px 30px -4px rgba(0,0,0,0.15)"}}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-[10px]" style={{color: isDark ? "#64748b" : "#9ca3af"}}>Invoice Paid</p>
                    <p className="font-bold text-sm" style={{color: isDark ? "#fff" : "#111827", fontFamily: "JetBrains Mono, monospace"}}>$4,200</p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-3 -right-3 sm:-right-6 rounded-xl p-3.5 animate-float-solid z-10" style={{ animationDelay: "1.5s", background: isDark ? "#0f1729" : "#ffffff", border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)", boxShadow: isDark ? "0 25px 50px -12px rgba(0,0,0,0.5)" : "0 8px 30px -4px rgba(0,0,0,0.15)" }}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 bg-brand-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-brand-400" />
                  </div>
                  <div>
                    <p className="text-[10px]" style={{color: isDark ? "#64748b" : "#9ca3af"}}>Job Profit</p>
                    <p className="font-bold text-sm text-emerald-400" style={{ fontFamily: "JetBrains Mono, monospace" }}>+38%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ TRADES BAR ═══════ */}
      <section className="py-8 px-4 sm:px-6 border-y lp-border lp-section">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: Thermometer, label: "HVAC", color: "text-red-400" },
              { icon: Wrench, label: "Plumbing", color: "text-blue-400" },
              { icon: Plug, label: "Electrical", color: "text-amber-400" },
              { icon: Hammer, label: "General", color: "text-gray-500" },
              { icon: Paintbrush, label: "Painting", color: "text-purple-400" },
              { icon: Home, label: "Roofing", color: "text-brand-400" },
              { icon: TreePine, label: "Landscaping", color: "text-green-400" },
            ].map((trade) => (
              <div key={trade.label} className="flex items-center gap-2.5">
                <trade.icon className={`h-4 w-4 ${trade.color}`} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{color: "var(--lp-muted)"}}>{trade.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 2. PROBLEM SECTION ═══════ */}
      <section ref={problemRef} className="py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.04),transparent_70%)]" />
        <div className="container mx-auto max-w-4xl relative text-center">
          <div className={`transition-all duration-700 ${problemInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 text-red-400 text-xs font-semibold uppercase tracking-widest mb-6">
              <AlertTriangle className="h-4 w-4" />
              The Hard Truth
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 leading-tight" data-testid="problem-headline">
              Where Most Contractors <span className="text-red-400">Struggle</span>
            </h2>
            <p className="mb-10 max-w-2xl mx-auto" style={{color: "var(--lp-subtle)"}}>
              You're great at the work. But the business side? That's where things get complicated.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12">
              {[
                { icon: DollarSign, title: "Inconsistent Pricing", text: "You're guessing on pricing without data to back up your numbers." },
                { icon: MessageSquare, title: "Miscommunication", text: "Texts, voicemails, sticky notes. Your crew doesn't know what's happening until they show up." },
                { icon: FileText, title: "Lost Paperwork", text: "Quotes go missing. Invoices pile up. You're chasing payments instead of running jobs." },
                { icon: BarChart3, title: "No Job Visibility", text: "You finished the job, but did the numbers add up? Most contractors have no idea." },
              ].map((item, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={`flex items-start gap-4 text-left lp-card rounded-xl p-5 border hover:border-red-500/20 transition-all duration-500 ${problemInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                    <item.icon className="h-5 w-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1" style={{color: "var(--lp-text)"}}>{item.title}</p>
                    <p className="text-xs leading-relaxed" style={{color: "var(--lp-subtle)"}}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-lg sm:text-xl font-medium" style={{color: "var(--lp-subtle)"}}>
              You're working 60-hour weeks &mdash; <span style={{color: "var(--lp-text)"}} className="font-semibold">but flying blind on the numbers.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ 3. SOLUTION SECTION ═══════ */}
      <section ref={solutionRef} className="py-14 sm:py-20 px-4 sm:px-6 lp-section relative">
        <div className="container mx-auto max-w-7xl">
          <div className={`text-center mb-10 transition-all duration-700 ${solutionInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 text-brand-400 text-xs font-semibold uppercase tracking-widest mb-4">
              <Sparkles className="h-4 w-4" />
              The Fix
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4" data-testid="solution-headline">
              One System. <span className="text-brand-400">Total Control.</span>
            </h2>
            <p className="max-w-2xl mx-auto" style={{color: "var(--lp-subtle)"}}>
              SkilledShark puts your jobs, crew, and customers in one place &mdash; so you can stop reacting and start running your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Calendar,
                title: "Jobs & Scheduling",
                desc: "Create jobs, schedule calls, and dispatch your crew with drag-and-drop. No more double-booking or missed appointments.",
                accent: "from-blue-500 to-cyan-500",
                iconBg: "bg-blue-500/10",
                iconColor: "text-blue-400",
              },
              {
                icon: Users,
                title: "Crew Management",
                desc: "Your team sees their schedule, updates job status, and logs hours from their phone. No more 'where do I go next?' calls.",
                accent: "from-violet-500 to-purple-500",
                iconBg: "bg-violet-500/10",
                iconColor: "text-violet-400",
              },
              {
                icon: TrendingUp,
                title: "Pricing & Job Costing",
                desc: "Know your numbers on every job before you start. TradeIQ analyzes your pricing and helps you stay competitive.",
                accent: "from-emerald-500 to-teal-500",
                iconBg: "bg-emerald-500/10",
                iconColor: "text-emerald-400",
              },
              {
                icon: Receipt,
                title: "Invoicing & Payments",
                desc: "Turn completed jobs into invoices in one click. Automated reminders chase payments so you don't have to.",
                accent: "from-brand-500 to-amber-500",
                iconBg: "bg-brand-500/10",
                iconColor: "text-brand-400",
              },
            ].map((pillar, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`lp-card rounded-2xl border p-6 hover:border-opacity-20 transition-all duration-500 group ${solutionInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`h-12 w-12 ${pillar.iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <pillar.icon className={`h-6 w-6 ${pillar.iconColor}`} />
                </div>
                <h3 className="text-base font-bold mb-2" style={{color: "var(--lp-text)"}}>{pillar.title}</h3>
                <p className="text-sm leading-relaxed" style={{color: "var(--lp-subtle)"}}>{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 4. FEATURES → OUTCOMES ═══════ */}
      <section id="features" ref={featuresRef} className="py-14 sm:py-20 px-4 sm:px-6 relative">
        <div className="container mx-auto max-w-6xl">
          <div className={`text-center mb-10 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 text-brand-400 text-xs font-semibold uppercase tracking-widest mb-4">
              <Target className="h-4 w-4" />
              Features That Work as Hard as You Do
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              What SkilledShark <span className="text-brand-400">Actually Does For You</span>
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { icon: DollarSign, outcome: "Know what to charge before you underbid", detail: "TradeIQ analyzes your past jobs and suggests competitive pricing - not a guess.", color: "text-emerald-400", bg: "bg-emerald-500/10" },
              { icon: Users, outcome: "Your crew knows exactly where to go - without calling you", detail: "Techs see their full schedule, job details, customer info, and directions on their phone.", color: "text-blue-400", bg: "bg-blue-500/10" },
              { icon: Layers, outcome: "Track every job from quote to payment", detail: "Full pipeline: Lead, Quote, Contract, Work, Invoice, Paid. Nothing falls through the cracks.", color: "text-violet-400", bg: "bg-violet-500/10" },
              { icon: TrendingUp, outcome: "Track every job from start to finish", detail: "Real-time job costing per job. See your margins before, during, and after every call.", color: "text-brand-400", bg: "bg-brand-500/10" },
              { icon: Receipt, outcome: "Get paid in days, not months", detail: "One-click invoicing, automatic reminders, and online payments. Money hits your account faster.", color: "text-cyan-400", bg: "bg-cyan-500/10" },
              { icon: TradeIQIcon, outcome: "Get instant answers on pricing, troubleshooting, and estimates", detail: "TradeIQ is your AI-powered assistant that knows your trade and your numbers.", color: "text-cyan-400", bg: "bg-cyan-500/10" },
              { icon: BarChart, outcome: "See which techs and jobs perform best", detail: "Revenue per tech, performance by job type, average ticket size - all at a glance.", color: "text-amber-400", bg: "bg-amber-500/10" },
              { icon: MessageSquare, outcome: "Keep your whole team on the same page", detail: "In-app messaging, job-based threads, @mentions. No more scattered text chains.", color: "text-pink-400", bg: "bg-pink-500/10" },
            ].map((item, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`flex items-start gap-5 p-5 rounded-xl lp-card border transition-all duration-500 ${featuresInView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className={`h-11 w-11 ${item.bg} rounded-lg flex items-center justify-center shrink-0`}>
                  <item.icon className={`h-5 w-5 ${item.color}`} />
                </div>
                <div>
                  <p className="text-sm sm:text-base font-semibold mb-0.5" style={{color: "var(--lp-text)"}}>{item.outcome}</p>
                  <p className="text-xs sm:text-sm leading-relaxed" style={{color: "var(--lp-subtle)"}}>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TRADEIQ SHOWCASE ═══════ */}
      <section ref={tradeIQRef} className="py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.04] via-transparent to-cyan-500/[0.02]" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-cyan-500/[0.06] rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-blue-500/[0.04] rounded-full blur-[150px]" />
        
        <div className="container mx-auto max-w-6xl relative">
          {/* Header */}
          <div className={`text-center mb-10 transition-all duration-700 ${tradeIQInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <TradeIQIcon className="h-4 w-4 text-cyan-400" />
              <span className="text-cyan-400 text-xs font-semibold uppercase tracking-widest">AI Operations Manager</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Meet <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">TradeIQ</span> — Your AI Operations Manager
            </h2>
            <p className="max-w-2xl mx-auto text-base sm:text-lg" style={{color: "var(--lp-muted)"}}>
              TradeIQ watches your business around the clock, answers trade questions on the job site, and gives you advice based on your actual data — not generic tips.
            </p>
          </div>

          {/* Three Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-10">
            {[
              {
                icon: Eye,
                title: "Always Watching",
                description: "TradeIQ monitors your jobs 24/7 and alerts you to what needs attention before things fall through the cracks.",
                accent: "from-cyan-500 to-teal-500",
                delay: "delay-0",
              },
              {
                icon: MessageSquare,
                title: "Answers On The Job Site",
                description: "Ask TradeIQ anything about electrical code, plumbing code, or HVAC standards and get instant accurate answers.",
                accent: "from-cyan-500 to-blue-500",
                delay: "delay-150",
              },
              {
                icon: Brain,
                title: "Knows Your Business",
                description: "Chat with TradeIQ about your actual jobs, customers, and revenue — not generic advice, your real data.",
                accent: "from-blue-500 to-indigo-500",
                delay: "delay-300",
              },
            ].map((feature, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`group relative rounded-2xl border border-cyan-500/15 backdrop-blur-sm p-8 hover:border-cyan-500/30 hover:shadow-[0_0_30px_-5px_rgba(6,182,212,0.15)] transition-all duration-500 ${feature.delay} ${tradeIQInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: tradeIQInView ? `${i * 150}ms` : "0ms", background: "var(--lp-card)" }}
              >
                {/* Icon */}
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${feature.accent} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                {/* Content */}
                <h3 className="text-lg font-bold mb-2" style={{color: "var(--lp-text)"}}>{feature.title}</h3>
                <p className="text-sm leading-relaxed" style={{color: "var(--lp-muted)"}}>{feature.description}</p>
                {/* TradeIQ badge */}
                <div className="absolute top-4 right-4 opacity-30 group-hover:opacity-60 transition-opacity">
                  <TradeIQIcon className="h-5 w-5 text-cyan-400" />
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className={`text-center transition-all duration-700 delay-500 ${tradeIQInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Link to="/signup">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all duration-300 gap-2" data-testid="tradeiq-cta">
                <TradeIQIcon className="h-5 w-5" />
                See TradeIQ In Action
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ 5. DASHBOARD PREVIEW ═══════ */}
      <section ref={previewRef} className="py-14 sm:py-20 px-4 sm:px-6 lp-section relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-500/[0.03] rounded-full blur-[200px]" />
        <div className="container mx-auto max-w-6xl relative">
          <div className={`text-center mb-12 transition-all duration-700 ${previewInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Everything You Need &mdash; <span className="text-brand-400">At a Glance</span>
            </h2>
            <p className="max-w-2xl mx-auto" style={{color: "var(--lp-subtle)"}}>
              Your sales, jobs, crew, and financials on one dashboard. Open SkilledShark in the morning and know exactly where your business stands.
            </p>
          </div>

          <div className={`max-w-4xl mx-auto transition-all duration-1000 ${previewInView ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="relative cursor-pointer group" onClick={() => setTourOpen(true)}>
              <DashboardMockup />
              <div className="absolute inset-0 rounded-xl bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-white rounded-full p-4 shadow-xl"><Play className="h-8 w-8 text-brand-500 fill-brand-500" /></div>
              </div>
            </div>
          </div>

          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 transition-all duration-700 delay-300 ${previewInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {[
              { icon: BarChart3, label: "Sales & Revenue" },
              { icon: Calendar, label: "Job Schedule" },
              { icon: Users, label: "Crew Tracker" },
              { icon: TrendingUp, label: "Profit & KPIs" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-3 rounded-lg lp-card border">
                <item.icon className="h-4 w-4 text-brand-400 shrink-0" />
                <span className="text-xs font-medium" style={{color: "var(--lp-muted)"}}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ STATS BAR ═══════ */}
      {/* Reserved for real metrics once available */}

      {/* ═══════ 6. SOCIAL PROOF / AUTHORITY ═══════ */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 relative">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-brand-400 text-xs font-semibold uppercase tracking-widest mb-6">
                <ShieldCheck className="h-4 w-4" />
                Why SkilledShark
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 leading-tight">
                Built by people who've <span className="text-brand-400">actually run a skilled trades business.</span>
              </h2>
              <p className="leading-relaxed mb-8" style={{color: "var(--lp-muted)"}}>
                We didn't build SkilledShark in a tech startup office. We built it in the field, solving real problems that cost real contractors real money. This isn't bloated enterprise software. It's the tool your business actually needs.
              </p>
              <div className="space-y-4">
                {[
                  "Designed for skilled trades, not tech companies",
                  "Simpler than the big guys, smarter than a spreadsheet",
                  "Affordable enough for a two-man crew, powerful enough for 50 techs",
                  "Your data is yours. No lock-in contracts.",
                ].map((point, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-400 shrink-0" />
                    <span className="text-sm" style={{color: "var(--lp-muted)"}}>{point}</span>
                  </div>
                ))}
              </div>
          </div>
        </div>
      </section>

      {/* ═══════ 7. PRICING ═══════ */}
      <section id="pricing" ref={pricingRef} className="py-14 sm:py-20 px-4 sm:px-6 lp-section relative">
        <div className="container mx-auto max-w-7xl">
          <div className={`text-center mb-10 transition-all duration-700 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 text-brand-400 text-xs font-semibold uppercase tracking-widest mb-4">
              <CircleDollarSign className="h-4 w-4" />
              Pricing
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Simple Pricing. <span className="text-brand-400">Serious Value.</span>
            </h2>
            <p className="max-w-xl mx-auto" style={{color: "var(--lp-subtle)"}}>
              No hidden fees. No long-term contracts. Start free for 7 days, cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pricingTiers.map((tier, i) => (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 flex flex-col transition-all duration-500 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${
                  tier.highlighted
                    ? "bg-gradient-to-b from-brand-500/10 to-transparent border-2 border-brand-500 shadow-lg shadow-brand-500/10"
                    : "lp-card border"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`pricing-tier-${tier.id}`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    {tier.badge}
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="text-lg font-bold mb-1" style={{color: "var(--lp-text)"}}>{tier.name}</h3>
                  <p className="text-xs" style={{color: "var(--lp-subtle)"}}>{tier.tagline}</p>
                </div>
                <div className="mb-5">
                  <span className="text-4xl font-extrabold" style={{ fontFamily: "JetBrains Mono, monospace", color: "var(--lp-text)" }}>${tier.price}</span>
                  <span className="text-sm ml-1" style={{color: "var(--lp-subtle)"}}>/mo</span>
                </div>
                <p className="text-xs mb-6 leading-relaxed" style={{color: "var(--lp-subtle)"}}>{tier.description}</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle className={`h-4 w-4 shrink-0 mt-0.5 ${tier.highlighted ? "text-brand-400" : "text-emerald-500/70"}`} />
                      <span className="text-xs" style={{color: "var(--lp-muted)"}}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-auto">
                  <Button
                    className={`w-full font-semibold ${
                      tier.highlighted
                        ? "bg-brand-500 hover:bg-brand-600 text-white"
                        : isDark ? "bg-white/5 hover:bg-white/10 text-white border border-white/10" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                    }`}
                    data-testid={`pricing-cta-${tier.id}`}
                  >
                    {tier.cta}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-600 mt-8">
            All plans include a 7-day free trial. No credit card required to start. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ═══════ 8. FINAL CTA ═══════ */}
      <section ref={ctaRef} className="py-24 sm:py-32 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(249,115,22,0.08),transparent_60%)]" />
        <div className={`container mx-auto max-w-3xl text-center relative transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight" data-testid="cta-headline">
            Stop Guessing.{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 to-brand-300">Start Running Your Business Right.</span>
          </h2>
          <p className="text-base sm:text-lg mb-10 max-w-2xl mx-auto leading-relaxed" style={{color: "var(--lp-subtle)"}}>
            Join thousands of contractors who replaced the chaos with one simple system. See your profit, manage your crew, and get paid &mdash; all from one screen.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/signup">
              <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white gap-2 h-14 px-10 text-lg font-bold" data-testid="cta-signup-btn">
                Get Started Free <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className={`h-14 px-10 text-lg gap-2 font-medium ${isDark ? "border-white/10 text-white hover:bg-white/5" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
              onClick={() => setTourOpen(true)}
              data-testid="cta-demo-btn"
            >
              <Play className="h-5 w-5 text-brand-400" />
              See it in Action
            </Button>
          </div>

          <p className="text-sm" style={{color: "var(--lp-subtle)"}}>
            <Clock className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
            Setup takes less than 5 minutes &middot; No credit card required
          </p>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="py-14 px-4 sm:px-6 border-t lp-border" style={{borderColor: "var(--lp-border)"}}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={isDark ? "/logo-dark.png?v=3" : "/logo-light.png?v=3"} alt="SkilledShark" className="h-10 w-auto object-contain" />
              </div>
              <p className="text-sm max-w-md leading-relaxed" style={{color: "var(--lp-subtle)"}}>
                Run your entire skilled trades business from one screen. Built by skilled tradespeople, for skilled tradespeople. Simpler. Smarter. More efficient.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4 uppercase tracking-wider text-[11px]" style={{color: "var(--lp-muted)"}}>Product</h4>
              <ul className="space-y-2.5 text-sm" style={{color: "var(--lp-subtle)"}}>
                <li><button onClick={() => setTourOpen(true)} className="hover:text-brand-500 transition-colors">Features</button></li>
                <li><a href="#pricing" className="hover:text-brand-500 transition-colors">Pricing</a></li>
                <li><Link to="/mobile" className="hover:text-brand-500 transition-colors">Mobile App</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4 uppercase tracking-wider text-[11px]" style={{color: "var(--lp-muted)"}}>Company</h4>
              <ul className="space-y-2.5 text-sm" style={{color: "var(--lp-subtle)"}}>
                <li><Link to="/about" className="hover:text-brand-500 transition-colors">About</Link></li>
                <li><Link to="/contact" className="hover:text-brand-500 transition-colors">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-brand-500 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-brand-500 transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-4" style={{borderColor: "var(--lp-border)"}}>
            <p className="text-xs" style={{color: "var(--lp-subtle)"}}>
              &copy; {new Date().getFullYear()} SkilledShark, Inc. The operating system for skilled trades businesses.
            </p>
            <div className="flex gap-6 text-xs" style={{color: "var(--lp-subtle)"}}>
              <Link to="/privacy" className="hover:text-brand-500 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-brand-500 transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Product Tour Modal ── */}
      <ProductTourModal isOpen={tourOpen} onClose={() => setTourOpen(false)} />
      </div>
    </div>
  );
};

export default Landing;
